import { useTranslation } from 'react-i18next';
import {
  QButton,
  QCharacterCount,
  QFormControl,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QStack,
  QText,
  QTextarea,
} from '@qualio/ui-components';
import * as DisplayStrings from '../../../displayStrings';
import { FormMarkAsReviewed, FormMarkAsReviewedInput, FormMarkAsReviewedSchema } from '../../../types';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

type MarkAsReviewedModalProps = {
  isOpen: boolean;
  onSubmit: (payload: FormMarkAsReviewed) => void;
  onClose: () => void;
  isSubmitBtnLoading: boolean;
};

const MarkAsReviewedModal = ({ isOpen, onSubmit, onClose, isSubmitBtnLoading }: MarkAsReviewedModalProps) => {
  const { t } = useTranslation();
  const formMethods = useForm<FormMarkAsReviewedInput, unknown, FormMarkAsReviewed>({
    mode: 'onChange',
    resolver: zodResolver(FormMarkAsReviewedSchema),
  });
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = formMethods;
  const formHasErrors = !!Object.keys(errors).length;

  return (
    <QModal isOpen={isOpen} onClose={onClose} size="xl">
      <QModalHeader>
        <QText fontSize="20">{t('forms.labels.reviewStep')}</QText>
      </QModalHeader>
      <QModalBody pt="2px">
        <QStack direction="column" color="gray.700" spacing={4}>
          <QText fontSize="14" color="gray.800">
            Please mark this step as reviewed with an optional comment
          </QText>
          <Controller
            control={control}
            name="comment"
            render={({ field: { ref: _ref, ...field } }) => (
              <QFormControl label="Comment" error={errors.comment?.message} isInvalid={!!errors.comment}>
                <QTextarea {...field} aria-label="comment" data-cy="add-comment">
                  <QCharacterCount max={1000} />
                </QTextarea>
              </QFormControl>
            )}
          />
        </QStack>
      </QModalBody>
      <QModalActions>
        <QButton data-cy="cancel-review-button" onClick={onClose} variant="outline" isDisabled={isSubmitBtnLoading}>
          {DisplayStrings.Cancel}
        </QButton>
        <QButton
          data-cy="submit-review-button"
          onClick={handleSubmit(onSubmit)}
          isLoading={isSubmitBtnLoading}
          isDisabled={formHasErrors || isSubmitBtnLoading}
        >
          {t('forms.actions.markAsReviewed')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};

export default MarkAsReviewedModal;
